import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDecimal } from '@/components/sizing/advancedInsightsShared';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from '@/lib/charts/leaf';
import type { GpuMemoryBreakdown, PersonaKvBreakdown } from '@/types/narrative';
import { MemoryStick } from 'lucide-react';

type GpuMemoryAllocationChartProps = {
  gpuMemory: GpuMemoryBreakdown | null;
  personaKvBreakdowns: PersonaKvBreakdown[];
};

export const GpuMemoryAllocationChart: React.FC<GpuMemoryAllocationChartProps> = ({
  gpuMemory,
  personaKvBreakdowns,
}) => {
  if (!gpuMemory) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MemoryStick className="h-5 w-5 text-primary" />
            GPU Memory Allocation
          </CardTitle>
          <CardDescription>Memory breakdown per GPU</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            No memory data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const { weightsGiBPerGpu, runtimeOverheadGiB, hbmGiBPerGpu, usableKvGiBPerGpu } = gpuMemory;

  // Calculate total KV used by personas
  const totalPersonaKvGiB = personaKvBreakdowns.reduce((sum, p) => sum + p.avgKvGiBPerGpu, 0);
  const availableMemoryGiB = Math.max(0, usableKvGiBPerGpu - totalPersonaKvGiB);

  // Prepare data for horizontal stacked bar
  const memoryData = [
    {
      name: 'GPU Memory',
      weights: weightsGiBPerGpu,
      runtime: runtimeOverheadGiB,
      available: availableMemoryGiB,
      ...personaKvBreakdowns.reduce((acc, p, idx) => ({
        ...acc,
        [`persona${idx}`]: p.avgKvGiBPerGpu,
      }), {}),
    },
  ];

  // Colors for each segment
  const personaColors = [
    'hsl(142 71% 45%)', // green
    'hsl(38 92% 50%)',  // orange
    'hsl(196 100% 47%)', // cyan
    'hsl(270 60% 50%)',  // purple variant
  ];

  const utilizationPct = ((weightsGiBPerGpu + runtimeOverheadGiB + totalPersonaKvGiB) / hbmGiBPerGpu) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MemoryStick className="h-5 w-5 text-primary" />
          GPU Memory Allocation
        </CardTitle>
        <CardDescription>
          {formatDecimal(weightsGiBPerGpu, 1)} GiB model + {formatDecimal(runtimeOverheadGiB, 1)} GiB runtime + {formatDecimal(totalPersonaKvGiB, 1)} GiB KV cache
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Memory Utilization:</span>
          <span className="font-semibold text-foreground">{formatDecimal(utilizationPct, 1)}% of {formatDecimal(hbmGiBPerGpu, 0)} GiB</span>
        </div>

        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={memoryData}
            layout="vertical"
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              type="number" 
              domain={[0, hbmGiBPerGpu]}
              tickFormatter={(value) => `${formatDecimal(value, 0)} GiB`}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              content={({ payload }) => {
                if (!payload || payload.length === 0) return null;
                return (
                  <div className="rounded-lg border border-border bg-background p-3 shadow-lg">
                    <div className="space-y-1">
                      {payload.map((entry: any, index: number) => (
                        <div key={index} className="flex items-center justify-between gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                            <span className="text-muted-foreground">{entry.name}:</span>
                          </div>
                          <span className="font-medium">{formatDecimal(entry.value, 2)} GiB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="weights" stackId="a" fill="hsl(270 60% 60%)" name="Model Weights" />
            <Bar dataKey="runtime" stackId="a" fill="hsl(207 100% 50%)" name="Runtime Overhead" />
            {personaKvBreakdowns.map((p, idx) => (
              <Bar
                key={`persona${idx}`}
                dataKey={`persona${idx}`}
                stackId="a"
                fill={personaColors[idx % personaColors.length]}
                name={`${p.personaName} KV`}
              />
            ))}
            <Bar dataKey="available" stackId="a" fill="hsl(var(--muted))" name="Available" />
          </BarChart>
        </ResponsiveContainer>

        {/* Legend */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: 'hsl(270 60% 60%)' }} />
            <span className="text-muted-foreground">Model Weights</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: 'hsl(207 100% 50%)' }} />
            <span className="text-muted-foreground">Runtime Overhead</span>
          </div>
          {personaKvBreakdowns.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: personaColors[idx % personaColors.length] }} />
              <span className="text-muted-foreground">{p.personaName} KV</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm bg-muted" />
            <span className="text-muted-foreground">Available</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
